import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, InputGroup, InputLeftElement, Icon,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiSearch } from 'react-icons/fi';
import api from '../api';

export default function EmailRulesModal({ isOpen, onClose }) {
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  // Form state
  const [matchType, setMatchType] = useState('email_address');
  const [matchValue, setMatchValue] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedContactEmail, setSelectedContactEmail] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');

  // Lookup data
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [contactFilter, setContactFilter] = useState('');
  const [projectTasks, setProjectTasks] = useState([]);

  const fetchRules = useCallback(async () => {
    try { const { data } = await api.get('/email/rules'); setRules(data); } catch(e) {}
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await api.get('/companies');
      setCompanies(data);
    } catch(e) {}
  }, []);

  const fetchContacts = useCallback(async (companyId) => {
    if (!companyId) { setContacts([]); return; }
    try {
      const { data } = await api.get(`/contacts?company_id=${companyId}`);
      setContacts(data);
    } catch(e) {}
  }, []);

  const fetchProjectTasks = useCallback(async (companyId) => {
    if (!companyId) { setProjectTasks([]); return; }
    try {
      const { data } = await api.get(`/projects?company_id=${companyId}`);
      // Flatten to project-task pairs, filter out done
      const items = [];
      for (const p of data) {
        if (p.is_done) continue;
        const tasks = p.tasks || [];
        if (tasks.length === 0) {
          items.push({ project_id: p.id, task_id: null, label: `${p.project_name || 'Untitled'}` });
        }
        for (const t of tasks) {
          if (t.is_complete) continue;
          items.push({
            project_id: p.id, task_id: t.id,
            label: `${p.project_name || 'Untitled'} — ${t.task_title || 'Task'}`
          });
        }
      }
      setProjectTasks(items);
    } catch(e) {}
  }, []);

  useEffect(() => { if (isOpen) { fetchRules(); fetchCompanies(); setShowForm(false); setEditingId(null); } }, [isOpen, fetchRules, fetchCompanies]);

  // When company changes, fetch contacts and project-tasks
  useEffect(() => {
    if (selectedCompanyId) {
      fetchContacts(selectedCompanyId);
      fetchProjectTasks(selectedCompanyId);
    } else {
      setContacts([]); setProjectTasks([]);
    }
  }, [selectedCompanyId, fetchContacts, fetchProjectTasks]);

  // Auto-populate rule name
  useEffect(() => {
    if (matchType === 'email_address' && selectedContactEmail && selectedTaskId) {
      const task = projectTasks.find(pt => pt.task_id === selectedTaskId);
      if (task) {
        setRuleName(`${selectedContactEmail} > ${task.label}`);
      }
    }
  }, [selectedContactEmail, selectedTaskId, matchType, projectTasks]);

  // When contact selected, set match value
  useEffect(() => {
    if (matchType === 'email_address' && selectedContactEmail) {
      setMatchValue(selectedContactEmail);
    }
  }, [selectedContactEmail, matchType]);

  const resetForm = () => {
    setMatchType('email_address'); setMatchValue(''); setRuleName('');
    setSelectedCompanyId(''); setSelectedContactEmail(''); setSelectedTaskId(''); setSelectedProjectId('');
    setCompanySearch(''); setContactFilter('');
  };

  const handleSave = async () => {
    if (!ruleName || !matchValue) { toast({ title: 'Name and match value required', status: 'warning', duration: 2000 }); return; }
    const taskItem = projectTasks.find(pt => pt.task_id === selectedTaskId);
    const payload = {
      name: ruleName,
      match_type: matchType,
      match_value: matchValue,
      target_task_id: selectedTaskId || null,
      target_project_id: taskItem?.project_id || selectedProjectId || null,
    };
    setSaving(true);
    try {
      if (editingId) { await api.put(`/email/rules/${editingId}`, payload); }
      else { await api.post('/email/rules', payload); }
      await fetchRules(); setShowForm(false); setEditingId(null); resetForm();
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const MATCH_LABELS = { email_address: 'Email Address', from_address: 'From Address', from_domain: 'From Domain', subject_contains: 'Subject Contains' };

  const filteredContacts = contacts.filter(c => {
    if (!contactFilter) return true;
    const q = contactFilter.toLowerCase();
    return (c.first_name || '').toLowerCase().includes(q) ||
      (c.last_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q);
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>Mail Rules
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => { resetForm(); setEditingId(null); setShowForm(!showForm); }}>{showForm ? 'Cancel' : 'Add'}</Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={3}>
                {/* Match Type */}
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Match Type</FormLabel>
                  <Select size="xs" value={matchType} onChange={e => { setMatchType(e.target.value); setMatchValue(''); setSelectedContactEmail(''); }}>
                    <option value="email_address">Email Address (via Company → Contact)</option>
                    <option value="from_address">From Address (exact, manual)</option>
                    <option value="from_domain">From Domain</option>
                    <option value="subject_contains">Subject Contains</option>
                  </Select>
                </FormControl>

                {/* Email Address flow: Company → Contact → Task */}
                {matchType === 'email_address' && (
                  <>
                    {/* Company search */}
                    <FormControl><FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
                      <Select size="xs" value={selectedCompanyId}
                        onChange={e => { setSelectedCompanyId(e.target.value); setSelectedContactEmail(''); setSelectedTaskId(''); }}
                        onFocus={() => { if (companies.length === 0) fetchCompanies(); }}>
                        <option value="">Select company...</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </Select>
                    </FormControl>

                    {/* Contact list */}
                    {selectedCompanyId && (
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Contact Email</FormLabel>
                        <InputGroup size="xs" mb={1}>
                          <InputLeftElement><Icon as={FiSearch} color="gray.400" boxSize={3} /></InputLeftElement>
                          <Input pl={7} placeholder="Filter contacts..." value={contactFilter}
                            onChange={e => setContactFilter(e.target.value)} />
                        </InputGroup>
                        <Box maxH="120px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" bg="white">
                          <Box px={2} py={1} fontSize="11px" cursor="pointer"
                            bg={selectedContactEmail === 'ALL' ? 'blue.100' : 'white'}
                            _hover={{ bg: 'blue.50' }}
                            onClick={() => setSelectedContactEmail('ALL')}>
                            <Text fontWeight="600">ALL contacts at this company</Text>
                          </Box>
                          {filteredContacts.map(c => (
                            <Box key={c.id} px={2} py={1} fontSize="11px" cursor="pointer"
                              bg={selectedContactEmail === c.email ? 'blue.100' : 'white'}
                              _hover={{ bg: 'blue.50' }}
                              onClick={() => { if (c.email) setSelectedContactEmail(c.email); }}>
                              <Flex justify="space-between">
                                <Text fontWeight="500">{c.first_name} {c.last_name || ''}</Text>
                                <Text color="gray.500" fontSize="10px">{c.email || '(no email)'}</Text>
                              </Flex>
                            </Box>
                          ))}
                          {filteredContacts.length === 0 && (
                            <Text fontSize="10px" color="gray.400" p={2}>No contacts found</Text>
                          )}
                        </Box>
                      </FormControl>
                    )}

                    {/* Project / Task selector */}
                    {selectedCompanyId && selectedContactEmail && (
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Route to Project — Task</FormLabel>
                        <Select size="xs" value={selectedTaskId}
                          onChange={e => setSelectedTaskId(e.target.value)}>
                          <option value="">General Inbox (no routing)</option>
                          {projectTasks.map((pt, i) => (
                            <option key={`${pt.project_id}-${pt.task_id}-${i}`} value={pt.task_id || ''}>
                              {pt.label}
                            </option>
                          ))}
                        </Select>
                        {projectTasks.length === 0 && (
                          <Text fontSize="9px" color="gray.400" mt={0.5}>No active tasks for this company</Text>
                        )}
                      </FormControl>
                    )}

                    {selectedContactEmail === 'ALL' && (
                      <Text fontSize="9px" color="orange.500">
                        This rule will match ALL contacts at the selected company. Match value will use the company domain.
                      </Text>
                    )}
                  </>
                )}

                {/* Manual match types */}
                {matchType !== 'email_address' && (
                  <HStack w="100%" spacing={2}>
                    <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Match Value *</FormLabel>
                      <Input size="xs" value={matchValue} onChange={e => setMatchValue(e.target.value)}
                        placeholder={matchType === 'from_domain' ? 'example.com' : matchType === 'subject_contains' ? 'keyword' : 'user@example.com'} /></FormControl>
                    <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Target Task ID</FormLabel>
                      <Input size="xs" type="number" value={selectedTaskId} onChange={e => setSelectedTaskId(e.target.value)} placeholder="Optional" /></FormControl>
                  </HStack>
                )}

                {/* Rule Name */}
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Rule Name</FormLabel>
                  <Input size="xs" value={ruleName} onChange={e => setRuleName(e.target.value)}
                    placeholder="Auto-generated or type custom name" />
                </FormControl>

                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={() => {
                    // If ALL selected, use from_domain match
                    if (matchType === 'email_address' && selectedContactEmail === 'ALL') {
                      const company = companies.find(c => c.id === selectedCompanyId);
                      // Try to extract domain from first contact
                      const firstWithEmail = contacts.find(c => c.email);
                      if (firstWithEmail) {
                        const domain = firstWithEmail.email.split('@')[1];
                        setMatchValue(domain);
                        setMatchType('from_domain');
                      }
                    }
                    handleSave();
                  }}
                  isLoading={saving} alignSelf="flex-end">{editingId ? 'Update' : 'Add Rule'}</Button>
              </VStack>
            </Box>
          )}

          {rules.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No rules configured. Emails will go to general inbox.</Text>
          ) : (
            <VStack spacing={1} align="stretch">
              {rules.map(r => (
                <Flex key={r.id} p={2} border="1px" borderColor={r.deleted_at ? 'red.200' : 'gray.100'} borderRadius="md"
                  align="center" justify="space-between" opacity={r.deleted_at ? 0.5 : 1}>
                  <Box>
                    <Flex align="center" gap={2}>
                      <Text fontSize="12px" fontWeight="600">{r.name}</Text>
                      <Badge fontSize="8px" colorScheme="purple">{MATCH_LABELS[r.match_type] || r.match_type}</Badge>
                      {!r.is_active && <Badge fontSize="8px" colorScheme="red">Inactive</Badge>}
                    </Flex>
                    <Text fontSize="10px" color="gray.500">
                      Match: "{r.match_value}"
                      {r.task_title ? ` → ${r.task_title}` : r.project_name ? ` → ${r.project_name}` : ' → General Inbox'}
                    </Text>
                  </Box>
                  <HStack spacing={0}>
                    <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                      onClick={async () => { await api.delete(`/email/rules/${r.id}`); fetchRules(); }} aria-label="Delete" />
                  </HStack>
                </Flex>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
